/**
 * Google Drive File Picker Component
 * Allows users to browse and select files from their Google Drive
 * to import into tickets/bookings
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import {
  Cloud,
  File,
  FileText,
  Image,
  Folder,
  Loader2,
  ChevronRight,
  ArrowLeft,
  Download,
  CloudOff,
  AlertTriangle,
  Copy,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// File type icons
const getFileIcon = (mimeType) => {
  if (!mimeType) return <File className="w-5 h-5 text-zinc-400" />;
  
  if (mimeType.includes('folder')) {
    return <Folder className="w-5 h-5 text-yellow-400" />;
  }
  if (mimeType.includes('image')) {
    return <Image className="w-5 h-5 text-pink-400" />;
  }
  if (mimeType.includes('pdf') || mimeType.includes('document')) {
    return <FileText className="w-5 h-5 text-red-400" />;
  }
  return <File className="w-5 h-5 text-sky-400" />;
};

// Format file size
const formatSize = (bytes) => {
  if (!bytes) return '';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

const DriveFilePicker = ({ 
  isOpen, 
  onClose, 
  onFileSelect,
  acceptedTypes = ['application/pdf', 'image/'],
  title = 'Select from Google Drive'
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [folderStack, setFolderStack] = useState([{ id: null, name: 'My Drive' }]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);

  // Check Drive connection status
  useEffect(() => {
    if (isOpen) {
      setErrorDetails(null);
      checkDriveStatus();
    }
  }, [isOpen]);

  const checkDriveStatus = async () => {
    setIsLoading(true);
    setErrorDetails(null);
    try {
      const response = await axios.get(`${API}/drive/status`, { 
        withCredentials: true 
      });
      setIsConnected(response.data.connected);
      
      if (response.data.connected) {
        await loadFiles();
      }
    } catch (error) {
      setIsConnected(false);
      const errMsg = error.response?.data?.detail || error.message || 'Unknown error';
      setErrorDetails(errMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const loadFiles = async (folderId = null) => {
    setIsLoading(true);
    setErrorDetails(null);
    try {
      const params = folderId ? { folder_id: folderId } : { show_all: true };
      const response = await axios.get(`${API}/drive/files`, {
        params,
        withCredentials: true
      });
      const fileList = response.data.files || [];
      setFiles(fileList);
      
      // Show info if empty
      if (fileList.length === 0 && !folderId) {
        setErrorDetails("No files found. This may be a permissions issue. Try disconnecting and reconnecting Google Drive from the Personal page to grant full read access.");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to load Drive files';
      setErrorDetails(errorMsg);
      if (error.response?.status === 401) {
        toast.error('Drive connection expired. Please reconnect.');
        setIsConnected(false);
      } else if (error.response?.status === 400) {
        toast.error('Please connect Google Drive first');
        setIsConnected(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const copyError = () => {
    if (errorDetails) {
      navigator.clipboard.writeText(errorDetails);
      toast.success('Error copied to clipboard');
    }
  };

  const handleConnectDrive = async () => {
    try {
      const response = await axios.get(`${API}/drive/connect`, { 
        withCredentials: true 
      });
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      if (error.response?.status === 503) {
        toast.error('Google Drive is not configured. Please contact support.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to connect Google Drive');
      }
    }
  };

  const handleFolderClick = (folder) => {
    setFolderStack([...folderStack, { id: folder.id, name: folder.name }]);
    loadFiles(folder.id);
    setSelectedFile(null);
  };

  const handleBackClick = () => {
    if (folderStack.length <= 1) return;
    
    const newStack = folderStack.slice(0, -1);
    setFolderStack(newStack);
    loadFiles(newStack[newStack.length - 1].id);
    setSelectedFile(null);
  };

  const handleFileClick = (file) => {
    if (file.mimeType?.includes('folder')) {
      handleFolderClick(file);
    } else {
      setSelectedFile(file);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    
    setImporting(true);
    setErrorDetails(null);
    try {
      // Download the file from Google Drive
      const response = await axios.get(`${API}/drive/download/${selectedFile.id}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Get the actual content type from the response or fallback
      const contentType = response.headers['content-type'] || selectedFile.mimeType || 'application/octet-stream';
      
      // Create a Blob
      const blob = new Blob([response.data], { type: contentType });
      const fileName = selectedFile.name || 'download';
      
      // If onFileSelect is provided, upload and attach to ticket/booking
      if (onFileSelect) {
        // Upload to backend
        const formData = new FormData();
        formData.append('file', blob, fileName);
        
        const uploadResponse = await axios.post(`${API}/upload`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        // Call parent with the uploaded file info
        onFileSelect({
          url: uploadResponse.data.url,
          fileName: fileName
        });
        
        toast.success(`Attached: ${fileName}`);
      } else {
        // Just download to device
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast.success(`Downloaded: ${fileName}`);
      }
      
      onClose();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to import file';
      setErrorDetails(`Import failed: ${errorMsg}`);
      toast.error('Failed to import file from Drive');
    } finally {
      setImporting(false);
    }
  };

  // Filter files by accepted types
  const filteredFiles = files.filter(file => {
    // Always show folders
    if (file.mimeType?.includes('folder')) return true;
    
    // Check if file matches any accepted type
    return acceptedTypes.some(type => file.mimeType?.includes(type));
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="glass-card border-sky-500/20 max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Cloud className="w-5 h-5 text-sky-400" />
            {title}
          </DialogTitle>
        </DialogHeader>

        {/* Error Display */}
        {errorDetails && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-red-300">Error loading Google Drive</p>
                <p className="text-xs text-red-400/80 mt-1 break-all select-all">{errorDetails}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={copyError}
                className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 flex-shrink-0"
                title="Copy error"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {isLoading && !isConnected ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
          </div>
        ) : !isConnected ? (
          <div className="py-8 text-center">
            <CloudOff className="w-12 h-12 mx-auto text-zinc-500 mb-4" />
            <p className="text-zinc-400 mb-4">
              Connect your Google Drive to import files
            </p>
            <Button
              onClick={handleConnectDrive}
              className="btn-primary"
              data-testid="connect-drive-picker-btn"
            >
              <Cloud className="w-4 h-4 mr-2" />
              Connect Google Drive
            </Button>
          </div>
        ) : (
          <>
            {/* Breadcrumb navigation */}
            <div className="flex items-center gap-1 text-sm text-zinc-400 mb-2">
              {folderStack.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBackClick}
                  className="h-7 px-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              )}
              {folderStack.map((folder, index) => (
                <span key={folder.id || 'root'} className="flex items-center">
                  {index > 0 && <ChevronRight className="w-3 h-3 mx-1" />}
                  <span className={index === folderStack.length - 1 ? 'text-white' : ''}>
                    {folder.name}
                  </span>
                </span>
              ))}
            </div>

            {/* File list */}
            <ScrollArea className="h-[300px] pr-3">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-sky-400" />
                </div>
              ) : filteredFiles.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  No files found
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredFiles.map((file) => (
                    <div
                      key={file.id}
                      onClick={() => handleFileClick(file)}
                      className={`
                        flex items-center gap-3 p-3 rounded-lg cursor-pointer
                        transition-colors
                        ${selectedFile?.id === file.id 
                          ? 'bg-sky-500/20 border border-sky-500/40' 
                          : 'hover:bg-white/5 border border-transparent'
                        }
                      `}
                      data-testid={`drive-file-${file.id}`}
                    >
                      {getFileIcon(file.mimeType)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{file.name}</p>
                        <p className="text-xs text-zinc-500">
                          {file.mimeType?.includes('folder') 
                            ? 'Folder' 
                            : formatSize(file.size)
                          }
                        </p>
                      </div>
                      {file.mimeType?.includes('folder') && (
                        <ChevronRight className="w-4 h-4 text-zinc-500" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-white/10">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1 btn-outline"
              >
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                disabled={!selectedFile || selectedFile.mimeType?.includes('folder') || importing}
                className="flex-1 btn-primary"
                data-testid="import-drive-file-btn"
              >
                {importing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {onFileSelect ? 'Attach' : 'Download'}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DriveFilePicker;
